<script setup lang="ts">
/**
 * DonsPartout — grande carte sombre de l'accueil (Karim 2026-11-03) : en haut
 * à gauche trois pastilles (lien de paiement, QR code, Tap to Pay), en bas le
 * titre et le texte ; à droite la vidéo Donoor de 37 s, en boucle, fond
 * transparent (projet other/donoor-video-accueil).
 *
 * Deux fichiers pour la transparence : HEVC transparent (.mov) pour Safari,
 * WebM VP9 transparent pour Chrome, Firefox et Edge. Le .mov passe EN PREMIER
 * avec le type « video/quicktime » : Chrome ne le prend pas (sinon il lirait le
 * HEVC sans la transparence) et se rabat sur le WebM.
 *
 * Vidéo téléchargée seulement à l'approche (preload="none"), lue tant qu'elle
 * est à l'écran et que l'onglet est visible, en pause sinon. Mouvement réduit
 * demandé : une image fixe à la place.
 */
const localePath = useLocalePath();
const videoRef = ref<HTMLVideoElement | null>(null);

const pastilles = [
  { cle: "lien", icone: "i-lucide-link" },
  { cle: "qr", icone: "i-lucide-qr-code" },
  { cle: "tap", icone: "i-lucide-nfc" },
] as const;

let observateur: IntersectionObserver | null = null;
let aLEcran = false;

function majLecture() {
  const video = videoRef.value;
  if (!video) return;
  if (aLEcran && document.visibilityState === "visible") {
    video.muted = true;
    void video.play().catch(() => {});
  } else {
    video.pause();
  }
}

onMounted(() => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const video = videoRef.value;
  if (!video) return;
  observateur = new IntersectionObserver(
    ([entree]) => {
      aLEcran = !!entree?.isIntersecting;
      majLecture();
    },
    { threshold: 0.2 },
  );
  observateur.observe(video);
  document.addEventListener("visibilitychange", majLecture);
});

onBeforeUnmount(() => {
  observateur?.disconnect();
  document.removeEventListener("visibilitychange", majLecture);
});
</script>

<template>
  <section class="dp" aria-labelledby="dons-partout-titre">
    <div class="dp-contenu">
      <div class="dp-carte">
        <div class="dp-gauche">
          <ul class="dp-pastilles">
            <li v-for="p in pastilles" :key="p.cle" class="dp-pastille" :title="$t(`accueil.donsPartout.pastilles.${p.cle}`)">
              <UIcon :name="p.icone" class="dp-icone" aria-hidden="true" />
              <span class="sr-only">{{ $t(`accueil.donsPartout.pastilles.${p.cle}`) }}</span>
            </li>
          </ul>

          <div class="dp-textes">
            <!-- Police du texte courant : les h2 sont forcés en police d'affichage
                 par main.css, d'où le style en ligne (comme VitrineOutils). -->
            <h2
              id="dons-partout-titre"
              class="dp-titre"
              style="font-family: var(--font-body) !important; font-weight: 400 !important; line-height: 1.04 !important; letter-spacing: -0.035em !important; text-transform: none !important"
            >
              {{ $t("accueil.donsPartout.titre") }}
            </h2>
            <p class="dp-texte">
              {{ $t("accueil.donsPartout.texteAvant")
              }}<NuxtLink :to="localePath({ path: '/application-mobile-association' })" class="dp-lien">{{
                $t("accueil.donsPartout.lien")
              }}</NuxtLink
              >{{ $t("accueil.donsPartout.texteApres") }}
            </p>
          </div>
        </div>

        <div class="dp-droite">
          <video
            ref="videoRef"
            class="dp-video"
            muted
            loop
            playsinline
            preload="none"
            disablepictureinpicture
            aria-hidden="true"
            width="1200"
            height="1000"
          >
            <source src="/videos/accueil/dons-partout.mov" type='video/quicktime; codecs="hvc1"' />
            <source src="/videos/accueil/dons-partout.webm" type="video/webm" />
          </video>
          <img
            class="dp-image"
            src="/videos/accueil/dons-partout.webp"
            alt=""
            aria-hidden="true"
            width="1200"
            height="1000"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.dp {
  background: #fff;
  padding: 1rem 0 4.5rem;
}

.dp-contenu {
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 1.25rem;
}

/* La carte : carbone Donoor, éclairée en haut à gauche. */
.dp-carte {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  border-radius: 28px;
  overflow: hidden;
  color: #fff;
  background:
    radial-gradient(90% 120% at 0% 0%, rgba(255, 255, 255, 0.09) 0%, rgba(255, 255, 255, 0) 55%),
    linear-gradient(115deg, #26262a 0%, #18181b 45%, #0d0d0e 100%);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
}

.dp-gauche {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 2.5rem;
  padding: 2rem 1.5rem 0;
}

/* Pastilles blanches qui se chevauchent. */
.dp-pastilles {
  display: flex;
  margin: 0;
  padding: 0;
  list-style: none;
}

.dp-pastille {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 0 0 3px #1f1f22, 0 10px 24px -8px rgba(0, 0, 0, 0.6);
}

.dp-pastille + .dp-pastille {
  margin-left: -10px;
}

.dp-icone {
  width: 26px;
  height: 26px;
  color: #232323;
}

.dp-titre {
  margin: 0;
  max-width: 12ch;
  font-size: clamp(38px, 5.2vw, 76px);
  color: #fff;
}

.dp-texte {
  margin: 1.25rem 0 0;
  max-width: 34rem;
  font-size: clamp(16px, 1.35vw, 19px);
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.62);
}

.dp-lien {
  color: rgba(255, 255, 255, 0.88);
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-thickness: 1px;
  transition: color 0.2s ease;
}

.dp-lien:hover {
  color: #fff;
}

.dp-droite {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.75rem 1rem;
}

.dp-video,
.dp-image {
  display: block;
  width: 100%;
  max-width: 560px;
  height: auto;
  aspect-ratio: 6 / 5;
  background: transparent;
}

.dp-image {
  display: none;
}

@media (prefers-reduced-motion: reduce) {
  .dp-video {
    display: none;
  }
  .dp-image {
    display: block;
  }
}

/* Ordinateur : le texte à gauche (pastilles en haut, titre en bas), la vidéo à droite. */
@media (min-width: 1024px) {
  .dp-carte {
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
    min-height: 580px;
  }
  .dp-gauche {
    padding: 3rem 0 3.25rem 3.5rem;
  }
  .dp-pastille {
    width: 64px;
    height: 64px;
  }
  .dp-icone {
    width: 30px;
    height: 30px;
  }
  .dp-droite {
    padding: 1.5rem 2.5rem 1.5rem 1rem;
  }
  .dp-video,
  .dp-image {
    max-width: 680px;
  }
}
</style>
